import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import AppHeader from '../components/shell/AppHeader'
import { notificationGroupsFor, type NotificationGroup } from '../components/shell/notificationCatalog'
import { getTurnedOffKinds, setKindsOn } from '../components/shell/notificationPreferenceStore'
import type { NotificationEventKind } from '../components/shell/notificationStore'
import { supabase } from '../lib/supabase'
import { useProfileIdentity } from '../lib/useProfileIdentity'

const HOME: Record<string, string> = { agent: '/agent', supervisor: '/supervisor', administrator: '/admin', leadership: '/leadership' }

function ArrowLeftIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  )
}

/**
 * docs/product/notifications.md's "Preferences": the in-app notices an account
 * can turn off, grouped the way they read to the person. A group that has been
 * turned off stops being stored from then on; notices already received stay.
 */
export default function NotificationSettingsPage() {
  const identity = useProfileIdentity()
  const [userId, setUserId] = useState<string | null>(null)
  const [role, setRole] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [turnedOff, setTurnedOff] = useState<Set<NotificationEventKind>>(new Set())
  const [failed, setFailed] = useState(false)

  const load = useCallback(async (id: string) => {
    setTurnedOff(await getTurnedOffKinds(id))
    setLoaded(true)
  }, [])

  useEffect(() => {
    let cancelled = false
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      const id = session?.user.id
      if (!id || cancelled) return
      setUserId(id)
      const { data: profile } = await supabase.from('profiles').select('role').eq('id', id).single()
      if (cancelled) return
      setRole(profile?.role ?? null)
      await load(id)
    })
    return () => {
      cancelled = true
    }
  }, [load])

  const isOn = (group: NotificationGroup) => group.kinds.every((kind) => !turnedOff.has(kind))

  async function toggle(group: NotificationGroup) {
    if (!userId) return
    const on = !isOn(group)
    const landed = await setKindsOn(userId, group.kinds, on)
    setFailed(!landed)
    await load(userId)
  }

  const groups = notificationGroupsFor(role)

  return (
    <div className="mx-auto flex h-dvh w-full max-w-md flex-col border-line bg-canvas text-ink sm:border-x lg:max-w-none lg:border-x-0">
      <a
        href="#notification-settings-main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded focus:bg-canvas focus:px-4 focus:py-2 focus:font-semibold"
      >
        Skip to main content
      </a>

      <AppHeader initials={identity?.initials ?? ''} />

      <main id="notification-settings-main" tabIndex={0} className="flex flex-1 flex-col overflow-y-auto">
        <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col gap-6 p-4 lg:p-8">
          <Link
            to={role ? (HOME[role] ?? '/') : '/'}
            className="flex min-h-11 items-center gap-1.5 self-start rounded-control text-small font-bold text-ink-muted"
          >
            <ArrowLeftIcon />
            Back
          </Link>

          <div>
            <h1 className="text-h3 font-extrabold lg:text-h2">Notification Settings</h1>
            <p className="mt-0.5 text-small text-ink-muted">Choose which notices appear in your notification list. A notice you turn off is not stored from then on.</p>
          </div>

          {failed && (
            <p role="alert" className="text-caption font-semibold text-error">
              The change could not be saved. Try again.
            </p>
          )}

          {!loaded ? (
            <p role="status" className="text-small text-ink-muted">
              Loading&hellip;
            </p>
          ) : groups.length === 0 ? (
            <p className="text-small text-ink-muted">There are no notices on this account that can be turned off.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {groups.map((group) => (
                <li key={group.id} className="rounded-surface border border-line p-3">
                  <label className="flex min-h-11 items-start gap-3">
                    <input type="checkbox" checked={isOn(group)} onChange={() => toggle(group)} className="mt-0.5 size-5 flex-none" />
                    <span>
                      <span className="block text-small font-bold">{group.label}</span>
                      <span className="mt-0.5 block text-small text-ink-muted">{group.description}</span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
    </div>
  )
}
